import { GEOFENCE_KINDS, GEOFENCE_LIMITS, VEHICLE_TYPES, isSimplePolygon, validateGeofence, type Geofence, type LonLat, type Site } from "@fts/core";
import { Suspense, lazy, useMemo, useState, type FormEvent } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { LazyBoundary } from "../components/LazyBoundary";
import { Note } from "../components/ui";
import { useI18n, type MessageKey } from "../i18n";
import {
  DWELL_MAX_MINUTES,
  DWELL_MIN_MINUTES,
  GEOFENCE_ISSUE_KEY,
  draftFromGeofence,
  draftGeometry,
  draftToInput,
  dwellSeconds,
  emptyDraft,
  siteAnchor,
  type GeofenceDraft,
} from "../lib/geofences";
import { useSession } from "../state/session";

const GeofenceMap = lazy(() => import("./GeofenceMap"));

/** Create or edit a zone of a site: name, kind, shape drawn on the map, vehicle types, dwell limit, active. */
export function GeofenceFormDialog({
  site,
  sites,
  zone,
  siteZones,
  onSaved,
  onClose,
}: {
  site: Site;
  sites: Site[];
  /** Absent = new zone. */
  zone?: Geofence;
  /** The site's other zones (map context). */
  siteZones: Geofence[];
  onSaved: (g: Geofence) => void;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { provider, tenant } = useSession();
  const [d, setD] = useState<GeofenceDraft>(() => (zone ? draftFromGeofence(zone) : emptyDraft()));
  const [view, setView] = useState<LonLat | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [mapError, setMapError] = useState(false);
  const anchor = useMemo(() => siteAnchor(sites, site.id), [sites, site.id]);
  const context = useMemo(() => siteZones.filter((z) => z.id !== zone?.id), [siteZones, zone?.id]);
  const set = (patch: Partial<GeofenceDraft>) => setD((x) => ({ ...x, ...patch }));

  const input = draftToInput(d, { id: zone?.id, tenantId: tenant?.id ?? site.tenantId, siteId: site.id });
  const issues = validateGeofence(input, VEHICLE_TYPES);
  const has = (...codes: string[]) => submitted && issues.some((i) => codes.includes(i));
  const nameError = has("name_required", "name_too_long") ? t(GEOFENCE_ISSUE_KEY[issues.find((i) => i === "name_required" || i === "name_too_long")!]) : null;
  const dwellError = has("dwell_out_of_range") ? t("geofence.issue.dwell_out_of_range") : null;
  const radiusIssue = d.shape === "circle" ? issues.find((i) => i === "radius_out_of_range" || i === "too_narrow") : undefined;
  const radiusError = submitted && radiusIssue ? t(GEOFENCE_ISSUE_KEY[radiusIssue]) : null;
  const shapeIssue = issues.find((i) => ["too_few_vertices", "too_many_vertices", "self_intersecting", "too_narrow", "invalid_coordinate", "invalid_shape"].includes(i) && !(i === "too_narrow" && d.shape === "circle"));
  // Crossing edges are worth saying at once, not only after "Save".
  const crossing = d.shape === "polygon" && d.vertices.length >= 3 && !isSimplePolygon(d.vertices);
  const shapeError = crossing ? t("geofence.issue.self_intersecting") : submitted && shapeIssue ? t(GEOFENCE_ISSUE_KEY[shapeIssue]) : null;
  const target = view ?? anchor;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (issues.length > 0) return;
    setBusy(true);
    setError(null);
    try {
      onSaved(await provider.saveGeofence(input));
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const geometry = draftGeometry(d);

  return (
    <Dialog
      wide
      title={zone ? t("geofence.editTitle") : t("geofence.addTitle")}
      description={t("geofence.dialogHint", { site: site.name })}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("common.save")}
          </button>
        </>
      }
    >
      <FormField id="gf-name" label={t("geofence.name")} hint={t("geofence.nameHint")} error={nameError}>
        <input
          {...fieldAria("gf-name", { hint: true, error: nameError })}
          type="text"
          value={d.name}
          maxLength={GEOFENCE_LIMITS.nameMax}
          autoComplete="off"
          onChange={(e) => set({ name: e.target.value })}
          data-autofocus
        />
      </FormField>

      <fieldset className="fieldset">
        <legend>{t("geofence.kind")}</legend>
        <div className="zone-kinds">
          {GEOFENCE_KINDS.map((k) => (
            <label key={k} className={`zone-kind${d.kind === k ? " is-active" : ""}`}>
              <input type="radio" name="gf-kind" value={k} checked={d.kind === k} onChange={() => set({ kind: k })} />
              <span>
                <span className="zone-kind-title">
                  <span className={`zone-swatch zone-${k}`} aria-hidden="true" />
                  {t(`geofence.kind.${k}` as MessageKey)}
                </span>
                <span className="field-hint">{t(`geofence.kindHint.${k}` as MessageKey)}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {d.kind === "dwell" && (
        <FormField
          id="gf-dwell"
          label={t("geofence.dwellMinutes")}
          hint={t("geofence.dwellHint", {
            min: DWELL_MIN_MINUTES,
            max: DWELL_MAX_MINUTES,
            minutes: dwellSeconds(d.dwellMinutes) !== null ? d.dwellMinutes.trim() : "…",
          })}
          error={dwellError}
        >
          <input
            {...fieldAria("gf-dwell", { hint: true, error: dwellError })}
            type="number"
            inputMode="numeric"
            min={DWELL_MIN_MINUTES}
            max={DWELL_MAX_MINUTES}
            step={1}
            value={d.dwellMinutes}
            onChange={(e) => set({ dwellMinutes: e.target.value })}
          />
        </FormField>
      )}

      <fieldset className="fieldset">
        <legend>{t("geofence.shape")}</legend>
        <div className="zone-shape-switch">
          {(["polygon", "circle"] as const).map((s) => (
            <label key={s} className="check-row">
              <input type="radio" name="gf-shape" value={s} checked={d.shape === s} onChange={() => set({ shape: s })} />
              <span>{t(`geofence.shape.${s}`)}</span>
            </label>
          ))}
        </div>
        <p className="field-hint" id="gf-map-hint">
          {d.shape === "polygon" ? t("geofence.drawPolygonHint") : t("geofence.drawCircleHint")}
        </p>
        <div className="zone-map">
          {mapError ? (
            <p className="zone-map-error" role="alert">
              {t("geofence.mapError")}
            </p>
          ) : (
            <LazyBoundary>
            <Suspense
              fallback={
                <div className="loading" role="status">
                  <span className="spinner" aria-hidden="true" />
                  {t("common.loading")}
                </div>
              }
            >
              <GeofenceMap
                shape={d.shape}
                kind={d.kind}
                vertices={d.vertices}
                center={d.center}
                radiusM={geometry.radiusM}
                anchor={anchor}
                context={context}
                onAddPoint={(p) => (d.shape === "polygon" ? setD((x) => (x.vertices.length >= GEOFENCE_LIMITS.maxVertices ? x : { ...x, vertices: [...x.vertices, p] })) : set({ center: p }))}
                onMoveVertex={(i, p) => setD((x) => ({ ...x, vertices: x.vertices.map((v, j) => (j === i ? p : v)) }))}
                onRemoveVertex={(i) => setD((x) => ({ ...x, vertices: x.vertices.filter((_, j) => j !== i) }))}
                onMoveCenter={(p) => set({ center: p })}
                onViewChange={setView}
                label={t("geofence.mapLabel")}
                vertexLabel={(i) => t("geofence.vertexLabel", { n: i + 1 })}
                centerLabel={t("geofence.centerLabel")}
                onError={() => setMapError(true)}
              />
            </Suspense>
            </LazyBoundary>
          )}
        </div>
        {d.shape === "polygon" ? (
          <div className="zone-tools">
            <span className="zone-count" aria-live="polite">
              {t("geofence.vertexCount", { count: d.vertices.length })}
            </span>
            <button
              type="button"
              className="button button-secondary button-sm"
              disabled={!target || d.vertices.length >= GEOFENCE_LIMITS.maxVertices}
              onClick={() => target && setD((x) => ({ ...x, vertices: [...x.vertices, target] }))}
            >
              <Icon name="plus" size={15} />
              {t("geofence.addVertexAtCenter")}
            </button>
            <button type="button" className="button button-secondary button-sm" disabled={d.vertices.length === 0} onClick={() => setD((x) => ({ ...x, vertices: x.vertices.slice(0, -1) }))}>
              {t("geofence.undoVertex")}
            </button>
            <button type="button" className="button button-secondary button-sm" disabled={d.vertices.length === 0} onClick={() => set({ vertices: [] })}>
              {t("geofence.clearVertices")}
            </button>
          </div>
        ) : (
          <div className="zone-tools">
            <span className="zone-count" aria-live="polite">
              {d.center ? t("geofence.centerSet") : t("geofence.centerMissing")}
            </span>
            <button type="button" className="button button-secondary button-sm" disabled={!target} onClick={() => target && set({ center: target })}>
              {t("geofence.centerAtMapCenter")}
            </button>
          </div>
        )}
        {d.shape === "circle" && (
          <FormField id="gf-radius" label={t("geofence.radius")} hint={t("geofence.radiusHint", { min: GEOFENCE_LIMITS.minRadiusM, max: GEOFENCE_LIMITS.maxRadiusM })} error={radiusError}>
            <input
              {...fieldAria("gf-radius", { hint: true, error: radiusError })}
              type="number"
              inputMode="decimal"
              min={GEOFENCE_LIMITS.minRadiusM}
              max={GEOFENCE_LIMITS.maxRadiusM}
              step={1}
              value={d.radius}
              onChange={(e) => set({ radius: e.target.value })}
            />
          </FormField>
        )}
        {shapeError && (
          <p className="field-error" role="alert">
            {shapeError}
          </p>
        )}
      </fieldset>

      <fieldset className="fieldset">
        <legend>{t("geofence.vehicleTypes")}</legend>
        <div className="zone-types">
          {VEHICLE_TYPES.map((vt) => (
            <label key={vt} className="check-row">
              <input
                type="checkbox"
                checked={d.vehicleTypes.includes(vt)}
                onChange={(e) => setD((x) => ({ ...x, vehicleTypes: e.target.checked ? [...x.vehicleTypes, vt] : x.vehicleTypes.filter((y) => y !== vt) }))}
              />
              <span>{t(`vehicleType.${vt}`)}</span>
            </label>
          ))}
        </div>
        <p className="field-hint">{t("geofence.vehicleTypesHint")}</p>
      </fieldset>

      <label className="check-row">
        <input type="checkbox" checked={d.active} onChange={(e) => set({ active: e.target.checked })} />
        <span>
          {t("geofence.activeLabel")}
          <span className="field-hint">{t("geofence.activeHint")}</span>
        </span>
      </label>

      <Note>{t("geofence.gnssNote")}</Note>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

export function DeleteGeofenceDialog({ zone, onDeleted, onClose }: { zone: Geofence; onDeleted: () => void; onClose: () => void }) {
  const { t } = useI18n();
  const { provider } = useSession();
  return (
    <ConfirmDialog
      title={t("geofence.deleteTitle")}
      confirmLabel={t("common.delete")}
      danger
      onClose={onClose}
      onConfirm={async () => {
        await provider.deleteGeofence(zone.id);
        onDeleted();
      }}
    >
      <p>{t("geofence.deleteBody", { name: zone.name })}</p>
    </ConfirmDialog>
  );
}
